import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaBullhorn } from 'react-icons/fa6';
import { Cite } from '@citation-js/core';
import '@citation-js/plugin-bibtex';

function Home() {
  const [newPublication, setNewPublication] = useState(null);

  useEffect(() => {
    const fetchAndParseBib = async () => {
      try {
        const bibtexKeyToDisplay = 'KleselMesser2026';

        const response = await fetch(`${process.env.PUBLIC_URL}/publications.bib`)
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const bibtexString = await response.text();
        const citation = new Cite(bibtexString);
        const entry = citation.data.find(e => e.id === bibtexKeyToDisplay);

        if (!entry) {
          console.error(`Error: BibTeX entry with key "${bibtexKeyToDisplay}" not found.`);
          return;
        }

        const publicationData = {
          authors: entry.author ? entry.author.map(a => `${a.given ? a.given + ' ' : ''}${a.family}`).join(', ') : '',
          year: entry.issued?.['date-parts']?.[0]?.[0]?.toString() || '',
          title: entry.title,
          venue: entry['container-title'],
          volume: entry.volume,
          issue: entry.issue || entry.number,
          doi: entry.DOI,
          articleLink: entry.URL || (entry.DOI ? `https://doi.org/${entry.DOI}` : undefined)
        };

        setNewPublication(publicationData);
      } catch (error) {
        console.error("Failed to fetch or parse publications.bib:", error);
      }
    };

    fetchAndParseBib();
  }, []);

  const highlightName = (authorsString) => {
    if (!authorsString) return 'N/A';
    const myName = "Michael Klesel";
    const myLastName = "Klesel";
    return authorsString.split(', ').map((author, index, arr) => {
      const isMyName = author.toLowerCase().includes(myName.toLowerCase()) ||
                       (author.includes(myLastName) && (author.includes("Michael") || author.includes("M.")));
      return (
        <span key={index}>
          {isMyName ? <strong style={{ color: 'var(--link-color)' }}>{author}</strong> : author}
          {index < arr.length - 1 ? ', ' : ''}
        </span>
      );
    });
  };

  return (
    <div className="container mt-5 mb-5">
      <div className="row justify-content-center text-center">
        <div className="col-lg-8 col-md-10 col-sm-12">
          
          <div className="mb-4">
            <img
              src="/img/Klesel_quadrat_web.jpg"
              alt="Michael Klesel"
              className="img-fluid rounded-circle shadow-sm mx-auto d-block"
              style={{
                width: '10em',
                height: '10em',
                objectFit: 'cover',
                border: '2px solid var(--border-color)'
              }}
            />
          </div>

          <div>
            <h1 className="h2 fw-bold mb-2">Michael Klesel</h1>
            <p className="text-muted mb-3" style={{ fontSize: '1.1rem' }}>
              Professor at Frankfurt University of Applied Sciences
            </p>
          </div>
        </div>
      </div>
      <div className="row justify-content-center mt-4">
        <div className="col-lg-8 col-md-10 col-sm-12">
          <div style={{ textAlign: 'justify' }}>
            <p>
              Welcome to my personal website. I am Michael, a professor at<a href="https://www.frankfurt-university.de/de/hochschule/fachbereich-3-wirtschaft-und-recht/kontakt/professor-innen/michael-klesel/" target="_blank" rel="noopener noreferrer"> Frankfurt University of Applied Sciences</a> with a strong interest in exploring and theorizing technology-related phenomena. My research centers on technology-related constructs such as <a href="https://www.emerald.com/insight/content/doi/10.1108/INTR-05-2020-0262/full/pdf?title=does-the-type-of-mind-wandering-matter-extending-the-inquiry-about-the-role-of-mind-wandering-in-the-it-use-experience" target="_blank" rel="noopener noreferrer"> mind wandering</a>, <a href="https://www.emerald.com/insight/content/doi/10.1108/IntR-11-2017-0418/full/pdf?title=a-test-for-multigroup-comparison-using-partial-least-squares-path-modeling" target="_blank" rel="noopener noreferrer">Structural Equation Modeling (SEM)</a>, and artificial intelligence and its specific aspects, including <a href="https://link.springer.com/content/pdf/10.1007/s12525-023-00640-9.pdf?pdf=button" target="_blank" rel="noopener noreferrer">Explainable AI (XAI)</a>. While I love doing behavioral science to gain a deeper understanding about human behavior, I am also greatly interested in design-relevant research and research that seeks to solve issues with innovative solutions. I am pleased that my research has been published in highly regarded Information Systems (IS) journals like <a href="https.www.emerald.com/insight/publication/issn/1066-2243" target="_blank" rel="noopener noreferrer">Internet Research</a>, <a href="https://www.springer.com/journal/12525" target="_blank" rel="noopener noreferrer">Electronic Markets</a>, and <a href="https://aisel.aisnet.org/cais/" target="_blank" rel="noopener noreferrer">Communications of the AIS</a>. For an overview of my work, please refer to the <Link to="/publications">publications</Link> section. Please do not hesitate to contact me for any inquiries.
            </p>
          </div>
        </div>
      </div>

      {newPublication ? (
        <div className="row justify-content-center mt-4 mb-5">
          <div className="col-lg-8 col-md-10 col-sm-12">
            <div className="card shadow-sm publication-highlight-card">
              <div className="card-header bg-transparent py-3">
                <h5 className="mb-0 d-flex align-items-center justify-content-center">
                  <FaBullhorn className="me-2" />
                  Featured Publication
                </h5>
              </div>
              <div className="card-body">

                <div className="publication-highlight-metadata text-center">
                  <h6 className="publication-title mb-1">{newPublication.title}</h6>
                  <p className="publication-authors text-muted small mb-2">
                    {highlightName(newPublication.authors)}
                  </p>
                  <p className="publication-venue small mb-2">
                    <em>{newPublication.venue}</em>
                    {newPublication.year && <span className="text-muted"> ({newPublication.year})</span>}
                    {newPublication.volume && <span className="text-muted">, Vol. {newPublication.volume}</span>}
                    {newPublication.issue && <span className="text-muted">, No. {newPublication.issue}</span>}
                    .
                  </p>
                  {newPublication.doi && (
                    <p className="publication-doi small mb-0">
                      <a href={`https://doi.org/${newPublication.doi}`} target="_blank" rel="noopener noreferrer" title="View on DOI.org">
                        https://doi.org/{newPublication.doi}
                      </a>
                    </p>
                  )}
                </div>

                <div className="publication-highlight-screenshot text-center mt-4">
                  <a href={newPublication.articleLink || `https://doi.org/${newPublication.doi}`} target="_blank" rel="noopener noreferrer" title={`View article: ${newPublication.title}`}>
                    <img
                      src="/img/kleselmesser2026.png"
                      alt={`Title page of "${newPublication.title}"`}
                      className="img-fluid rounded shadow-sm"
                      style={{
                        border: '1px solid var(--border-color)'
                      }}
                    />
                  </a>
                </div>

              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center mt-4"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>
      )}
    </div>
  );
}

export default Home;